import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
} from "reactstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchDepartments, fetchDesignations, fetchResourceApproval, updateResourceApproval, fetchJobDetailsApproval } from "store/actions";
import Loader from "common/pageLoader";
import CustomDatePicker from "common/CustomDatePicker";

const SelectcustomStyles = {
  control: (base) => ({
    ...base,
    maxHeight: 33.1,
    minHeight: 33.1,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: "0",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
};

const ResourceApproval = () => {
  const dispatch = useDispatch();
  const [filteredData, setFilteredData] = useState([]);
  const [selectedApprovals, setSelectedApprovals] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [jobDetails, setJobDetails] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [authDetails, setAuthDetails] = useState(null);

  const {
    departments,
    designations,
    loading,
    resourceApprovals,
    jobDetailsApproval,
    updateSuccess,
  } = useSelector((state) => ({
    departments: state.DepartmentMaster?.departments ?? [],
    designations: state.DesignationMaster?.designations ?? [],
    loading: state.DepartmentMaster?.loading || state.DesignationMaster?.loading || state.ResourceApprovalReducer?.loading,
    resourceApprovals: state.ResourceApprovalReducer?.resourceApprovals ?? [],
    jobDetailsApproval: state.ResourceApprovalReducer?.jobDetailsApproval ?? null,
    updateSuccess: state.ResourceApprovalReducer?.updateSuccess ?? false,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0]);
    const unitId = authUserDetails[0]?.unit_id;
    if (unitId) {
      dispatch(fetchDepartments(unitId));
      dispatch(fetchDesignations(unitId));
      const formData = new FormData();
      formData.append('dept_id', 0);
      formData.append('role_id', 0);
      formData.append('from_date', '');
      formData.append('to_date', '');
      formData.append('unit_id', unitId);
      dispatch(fetchResourceApproval(formData));
    }
  }, [dispatch]);

  useEffect(() => {
    if (resourceApprovals && resourceApprovals.length >= 0) {
      setFilteredData([...resourceApprovals].reverse());
    }
  }, [resourceApprovals]);

  useEffect(() => {
    if (jobDetailsApproval && jobDetailsApproval.length > 0) {
      setJobDetails(jobDetailsApproval[0]);
    }
  }, [jobDetailsApproval]);

  useEffect(() => {
    if (updateSuccess) {
      if (lastSearchParams) {
        dispatch(fetchResourceApproval(lastSearchParams));
      } else {
        dispatch(fetchResourceApproval(authDetails?.unit_id || ''));
      }
      setSelectedApprovals({});
    }
  }, [updateSuccess, dispatch, lastSearchParams, authDetails?.unit_id]);

  const departmentOptions = departments.map(dept => ({ 
    value: dept.departmentId, 
    label: dept.departmentname 
  }));

  const roleOptions = designations.map(desig => ({ 
    value: desig.desig_id, 
    label: desig.designation 
  }));

  const formik = useFormik({
    initialValues: {
      department: null,
      role: null,
      fromDate: "",
      toDate: "",
    },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append('dept_id', parseInt(values.department?.value) || 0);
      formData.append('role_id', parseInt(values.role?.value) || 0);
      formData.append('from_date', values.fromDate || '');
      formData.append('to_date', values.toDate || '');
      formData.append('unit_id', authDetails?.unit_id || '');
      setLastSearchParams(formData);
      dispatch(fetchResourceApproval(formData));
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    setLastSearchParams(null);
    dispatch(fetchResourceApproval(authDetails?.unit_id || ''));
    setSelectedApprovals({});
  };

  const handleCheckboxChange = (reqId, status) => {
    setSelectedApprovals(prev => ({
      ...prev,
      [reqId]: status
    }));
  };

  const handleRemarksChange = (reqId, value) => {
    setFilteredData(prev => prev.map(item =>
      item.req_id === reqId ? { ...item, remarks: value } : item
    ));
  };

  const handleUpdate = () => {
    const reqtArray = Object.entries(selectedApprovals).map(([req_id, req_approval_status]) => {
      const item = filteredData.find(d => d.req_id.toString() === req_id);
      return {
        req_id,
        req_approval_status: req_approval_status.toString(),
        req_approval_remarks: item?.remarks || ""
      };
    });

    if (reqtArray.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select at least one approval/rejection",
      });
      return;
    }

    const missingRemarks = reqtArray.filter(
      item => item.req_approval_status === "3" && !item.req_approval_remarks.trim()
    );

    if (missingRemarks.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Remarks",
        text: "Please provide remarks for all rejected items",
      });
      return;
    }

    const formData = new FormData();
    formData.append('lst_resource_rec_approval', JSON.stringify({ reqt: reqtArray }));
    formData.append("create_by", authDetails?.user_id);

    dispatch(updateResourceApproval(formData));
    setSelectedApprovals({});
  };

  const handleJobDetailsClick = (reqId) => {
    const formData = new FormData();
    formData.append('req_id', reqId);
    formData.append('unit_id', authDetails?.unit_id || '');
    dispatch(fetchJobDetailsApproval(formData));
    setModalOpen(true);
  };

  const columns = [
    { Header: "S No", Cell: ({ row, data }) => data.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
    {
      Header: "Approve",
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedApprovals[row.original.req_id] === 2}
          onChange={() => handleCheckboxChange(row.original.req_id, 2)}
        />
      ),
      className: "text-center"
    },
    {
      Header: "Reject",
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedApprovals[row.original.req_id] === 3}
          onChange={() => handleCheckboxChange(row.original.req_id, 3)}
        />
      ),
      className: "text-center"
    },
    {
      Header: "Remarks",
      Cell: ({ row }) => {
        const [value, setValue] = useState(row.original.remarks || "");

        const handleBlur = () => {
          if (value) {
            handleRemarksChange(row.original.req_id, value);
          }
        };

        return (
          <Input
            type="text"
            bsSize="sm"
            placeholder="Enter remarks"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
          />
        );
      }
    },
    {
      Header: "Job Details",
      Cell: ({ row }) => (
        <i 
          className="fas fa-file" 
          style={{ cursor: "pointer", fontSize: "18px" }}
          onClick={() => handleJobDetailsClick(row.original.req_id)}
        />
      ),
      className: "text-center"
    },
    { Header: "Department", accessor: "department" },
    { Header: "Role", accessor: "designation" },
    { Header: "Position", accessor: "position" },
    { Header: "Resource", accessor: "no_of_resource" },
    { Header: "Purpose", accessor: "purpose" },
    { Header: "Level", accessor: "emp_level" },
    { Header: "Requested By", accessor: "req_created_By" },
  ];

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Resource" breadcrumbItem="Approval" />
        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="">
                <Col md={3}>
                  <FormGroup>
                    <Label>
                      Department
                    </Label>
                    <Select
                      styles={SelectcustomStyles}
                      options={departmentOptions}
                      placeholder="--Select--"
                      value={formik.values.department}
                      onChange={(opt) => formik.setFieldValue("department", opt)}
                      onBlur={() => formik.setFieldTouched("department", true)}
                      className="hrms-select"
                      classNamePrefix="hrms-select"
                    />

                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>
                      Role
                    </Label>
                    <Select
                      styles={SelectcustomStyles}
                      options={roleOptions}
                      placeholder="--Select--"
                      value={formik.values.role}
                      onChange={(opt) => formik.setFieldValue("role", opt)}
                      onBlur={() => formik.setFieldTouched("role", true)}
                      className="hrms-select"
                      classNamePrefix="hrms-select"
                    />

                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>
                      From Date
                    </Label>
                    <CustomDatePicker
                      name="fromDate"
                      value={formik.values.fromDate}
                      onChange={(val) => formik.setFieldValue("fromDate", val)}
                      onBlur={() => formik.setFieldTouched("fromDate", true)}
                      placeholder="Select Date"
                    />

                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>
                      To Date
                    </Label>
                    <CustomDatePicker
                      name="toDate"
                      value={formik.values.toDate}
                      onChange={(val) => formik.setFieldValue("toDate", val)}
                      onBlur={() => formik.setFieldTouched("toDate", true)}
                      placeholder="Select Date"
                    />

                  </FormGroup>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col className="mob-center text-end">
                  <Button type="submit" className="btn button-search me-2 mt-2">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button
                    type="button"
                    className="btn btn-success me-2 mt-2"
                    onClick={handleUpdate}
                  >
                    <i className="fas fa-check me-1" /> Update
                  </Button>
                  <Button
                    type="button"
                    className="btn button-cancel mt-2"
                    onClick={handleCancel}
                  >
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={filteredData}
              isGlobalFilter={false}
              customPageSize={10}
            />
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setModalOpen(false)}>Job Details</ModalHeader>
        <ModalBody>
          {jobDetails && (
            <div style={{ borderTop: '1px solid #dee2e6' }}>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Department:</strong> {jobDetails.department}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Role:</strong> {jobDetails.role}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Position:</strong> {jobDetails.position}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>No of Resources:</strong> {jobDetails.no_of_resource}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Purpose:</strong> {jobDetails.purpose}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Replacement Of:</strong> {jobDetails.replacement_of}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Gender:</strong> {jobDetails.gender}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Expected Joining Date:</strong> {jobDetails.expected_join_date}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Level:</strong> {jobDetails.emp_level}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Experience:</strong> {jobDetails.experience || 'N/A'}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Job Type:</strong> {jobDetails.jobtype}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Keys & Responsibilities:</strong> {jobDetails.keys_responsibilites || 'N/A'}
              </div>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <strong>Qualifications:</strong> {jobDetails.qualifications || 'N/A'}
              </div>
              <div style={{ padding: '10px 0' }}>
                <strong>Skills:</strong> {jobDetails.skills || 'N/A'}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ResourceApproval;
